import React from "react";

import { Button, Checkbox, DatePicker, Input, InputNumber, Modal, Popconfirm, Radio, Table, message } from "antd";

import {
    _deleteSupplierPriceListLine,
    _getSupplierPriceListLines,
    _postSupplierPriceListLinesBulk,
    _putSupplierPriceListLine
} from "../../services";
import { EditableNumberCell } from "../term-contract/line-cells";
import { errorMessage, toDay, toNumberOrNull } from "../term-contract/helpers";
import LineDrawer from "./line-drawer";
import LinkedProduct from "./linked-product";
import { formatDate, formatTiers, lastPriceChange, money } from "./helpers";

const MUTED = "rgba(0,0,0,0.45)";
const TOOLBAR_ROW = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 12 };
const QUICK_DAYS = [30, 90];
const BULK_TITLES = {
    remove: n => `Remove ${n.toLocaleString()} line${n === 1 ? "" : "s"}`,
    setValidTo: n => `Set valid to on ${n.toLocaleString()} line${n === 1 ? "" : "s"}`
};

/** The lines list's query params; also the bulk "all matching" filter (without the sort). */
export const linesFilter = ({
    q,
    linked,
    priceChangedWithinDays,
    expired,
    dropped,
}) => ({
    q: q || undefined,
    linked: linked || undefined,
    priceChangedWithinDays: priceChangedWithinDays || undefined,
    expired: expired ? "true" : undefined,
    dropped: dropped ? "true" : undefined
});

/** Expanded row: every price the line has had, oldest first, and which upload set it. */
const PriceHistory = ({ line, uploadLabel }) => (
    <Table
        size="small"
        pagination={false}
        rowKey={(entry, index) => `${index}`}
        dataSource={line.priceHistory || []}
        style={{ maxWidth: 640 }}
        columns={[
            { title: "File date", dataIndex: "fileDate", render: date => formatDate({ date }) },
            { title: "Cost", dataIndex: "costPrice", align: "right", render: value => money({ value }) },
            { title: "List", dataIndex: "listPrice", align: "right", render: value => money({ value }) },
            { title: "From", dataIndex: "uploadId", render: uploadId => uploadId ? uploadLabel({ uploadId }) : "Edited by hand" }
        ]}
    />
);

/** Confirm dialog for one bulk action; setValidTo also asks for the date. */
class BulkModal extends React.Component {

    state = { value: null, running: false };

    run = async () => {
        this.setState({ running: true });
        try {
            await this.props.onRun({ value: this.state.value ? this.state.value.format("YYYY-MM-DD") : null }); // success unmounts this modal
        } catch (e) {
            this.setState({ running: false });
        }
    };

    render() {
        const { action, count, onCancel } = this.props;
        const { value, running } = this.state;
        return (
            <Modal
                visible
                title={BULK_TITLES[action](count)}
                okText={action === "remove" ? "Remove" : "Apply"}
                okType={action === "remove" ? "danger" : "primary"}
                okButtonProps={{ disabled: action === "setValidTo" && !value }}
                confirmLoading={running}
                onOk={this.run}
                onCancel={onCancel}
            >
                {action === "remove"
                    ? <p>Removed lines stop matching at once and leave this list. A later file with the same item brings it back.</p>
                    : <DatePicker autoFocus value={value} format="YYYY-MM-DD" onChange={v => this.setState({ value: v })} />}
            </Modal>
        );
    }
}

/**
 * The lines tab: filters, inline price / MOQ edit, the add / edit drawer (every field, tiers, product link), bulk remove or
 * set valid to, and each line's price history on expand. The profile calls `reload` / `add` through a ref.
 */
class PriceListLines extends React.Component {

    state = {
        lines: [], total: 0, page: 1, pageSize: 50,
        q: "", linked: "", priceChangedWithinDays: null, expired: false, dropped: false, sort: "",
        selectedIds: [], allMatching: false, bulkAction: null, editing: null, loading: false
    };

    componentDidMount() {
        this.load();
    }

    filter = () => linesFilter(this.state);

    /* a new page or filter drops the selection, so a bulk action never hits rows the user can't see */
    load = async ({
        page = this.state.page,
        changes = {},
    } = {}) => {
        const view = page !== this.state.page || Object.keys(changes).length > 0;
        this.setState({ ...changes, page, loading: true, ...(view ? { selectedIds: [], allMatching: false } : {}) });
        const next = { ...this.state, ...changes };
        const filter = linesFilter(next);
        /* only the newest request may write: the days box loads on every keystroke */
        const seq = this.seq = (this.seq || 0) + 1;
        try {
            const { data } = await _getSupplierPriceListLines({
                id: this.props.priceList._id,
                page,
                filter: { ...filter, sort: next.sort || undefined }
            });
            if (seq !== this.seq) return;
            const ids = new Set(data.lines.map(l => l._id));
            this.setState(state => ({
                lines: data.lines, total: data.total, pageSize: data.pageSize,
                selectedIds: state.selectedIds.filter(id => ids.has(id))
            }));
            /* the tab label is the list's line count, so only an unfiltered total reports it */
            const unfiltered = Object.keys(filter).every(k => filter[k] === undefined);
            if (unfiltered && this.props.onTotal) this.props.onTotal({ total: data.total });
        } catch (e) {
            if (seq === this.seq) message.error(errorMessage({ error: e }));
        } finally {
            if (seq === this.seq) this.setState({ loading: false });
        }
    };

    /* for the profile's actions */
    reload = () => this.load({ page: 1 });

    add = () => this.setState({ editing: "new" });

    /* rejects on failure so the inline cell stays open */
    saveField = async ({
        line,
        field,
        value,
    }) => {
        try {
            const { data } = await _putSupplierPriceListLine({ id: this.props.priceList._id, lineId: line._id, data: { [field]: toNumberOrNull({ value }) } });
            (data.warnings || []).forEach(warning => message.warning(warning));
            message.success("Saved");
            this.load();
        } catch (e) {
            message.error(errorMessage({ error: e }));
            throw e;
        }
    };

    remove = async ({
        line,
    }) => {
        try {
            await _deleteSupplierPriceListLine({ id: this.props.priceList._id, lineId: line._id });
            message.success("Line removed");
            this.load();
        } catch (e) {
            message.error(errorMessage({ error: e }));
        }
    };

    runBulk = async ({
        value,
    }) => {
        const { bulkAction, selectedIds, allMatching } = this.state;
        try {
            const { data } = await _postSupplierPriceListLinesBulk({
                id: this.props.priceList._id,
                action: bulkAction,
                target: allMatching ? { filter: this.filter() } : { lineIds: selectedIds },
                value: bulkAction === "setValidTo" ? value : undefined
            });
            this.setState({ bulkAction: null, selectedIds: [], allMatching: false });
            message.success(`${data.affected} line${data.affected === 1 ? "" : "s"} updated${data.skipped.length ? `, ${data.skipped.length} skipped` : ""}`);
            this.load();
        } catch (e) {
            message.error(errorMessage({ error: e }));
            throw e;
        }
    };

    columns = () => {
        const { readOnly } = this.props;
        const numberCell = ({
            field,
            label,
            render,
        }) => (value, line) => (
            <EditableNumberCell label={label} readOnly={readOnly} value={value}
                onSave={({ value: v }) => this.saveField({ line, field, value: v })}>
                {render({ value })}
            </EditableNumberCell>
        );
        return [
            { title: "Code", dataIndex: "supplierCode", render: v => v || "—" },
            { title: "Brand", dataIndex: "brand", render: v => v || "—" },
            { title: "Model", dataIndex: "model", render: v => v || "—" },
            { title: "Title", dataIndex: "title", width: 240, render: (title, line) => line.droppedAt ? <span>{title} <i style={{ color: MUTED }}>(dropped {formatDate({ date: line.droppedAt })})</i></span> : title },
            { title: "Spec", dataIndex: "spec", render: v => v || "—" },
            { title: "UOM", dataIndex: "uom" },
            { title: "Cost", dataIndex: "costPrice", align: "right", render: numberCell({ field: "costPrice", label: "cost price", render: money }) },
            { title: "List", dataIndex: "listPrice", align: "right", render: numberCell({ field: "listPrice", label: "list price", render: money }) },
            { title: "MOQ", dataIndex: "moq", align: "right", render: numberCell({ field: "moq", label: "moq", render: ({ value }) => value || "—" }) },
            { title: "Tiers", dataIndex: "tiers", render: tiers => <span style={{ fontSize: 12 }}>{formatTiers({ tiers }) || "—"}</span> },
            { title: "Remarks", dataIndex: "remarks", render: v => v || "—" },
            { title: "Valid", key: "valid", render: (_, line) => `${toDay({ date: line.validFrom })} – ${toDay({ date: line.validTo })}` },
            { title: "Last confirmed", dataIndex: "lastConfirmedAt", render: date => formatDate({ date }) },
            {
                title: "Linked product",
                key: "product",
                render: (_, line) => <LinkedProduct line={line} />
            },
            {
                title: "Last price change",
                key: "lastPriceChangeAt",
                sorter: true,
                sortDirections: ["descend"],
                sortOrder: this.state.sort ? "descend" : false,
                render: (_, line) => lastPriceChange({ line }) || <span style={{ color: MUTED }}>—</span>
            },
            ...(readOnly ? [] : [{
                title: "",
                key: "actions",
                width: 80,
                render: (_, line) => (
                    <span style={{ whiteSpace: "nowrap" }}>
                        <Button size="small" type="link" icon="edit" aria-label="Edit line" title="Edit line" onClick={() => this.setState({ editing: line })} />
                        <Popconfirm title="Remove this line? It stops matching at once." onConfirm={() => this.remove({ line })}>
                            <Button size="small" type="link" icon="delete" aria-label="Remove line" title="Remove line" style={{ color: MUTED }} />
                        </Popconfirm>
                    </span>
                )
            }])
        ];
    };

    renderBulkBar() {
        const { lines, total, selectedIds, allMatching } = this.state;
        const count = allMatching ? total : selectedIds.length;
        return (
            <div style={{ ...TOOLBAR_ROW, padding: "8px 14px", background: "#e6f4ff", border: "1px solid #91caff", borderRadius: 4 }}>
                <b>{allMatching ? `All ${total.toLocaleString()} lines matching this filter selected` : `${count.toLocaleString()} selected`}</b>
                {!allMatching && count === lines.length && total > lines.length && (
                    <a onClick={() => this.setState({ allMatching: true })}>Select all {total.toLocaleString()} matching this filter</a>
                )}
                <Button size="small" onClick={() => this.setState({ bulkAction: "setValidTo" })}>Set valid to…</Button>
                <Button size="small" type="danger" onClick={() => this.setState({ bulkAction: "remove" })}>Remove</Button>
                <div style={{ flex: 1 }} />
                <a onClick={() => this.setState({ selectedIds: [], allMatching: false })}>Clear selection</a>
            </div>
        );
    }

    render() {
        const { priceList, readOnly, uploadLabel } = this.props;
        const { lines, total, page, pageSize, loading, linked, priceChangedWithinDays, expired, dropped, selectedIds, allMatching, bulkAction, editing } = this.state;
        return (
            <div>
                <div style={TOOLBAR_ROW}>
                    <Input.Search
                        allowClear
                        placeholder="Search title, brand, model, code or spec"
                        style={{ width: 300, maxWidth: "100%" }}
                        onSearch={value => this.load({ page: 1, changes: { q: value.trim() } })}
                    />
                    <Radio.Group value={linked} onChange={e => this.load({ page: 1, changes: { linked: e.target.value } })}>
                        <Radio.Button value="">All</Radio.Button>
                        <Radio.Button value="true">Linked</Radio.Button>
                        <Radio.Button value="false">Unlinked</Radio.Button>
                    </Radio.Group>
                    <span>
                        Price changed within{" "}
                        <InputNumber
                            min={1}
                            precision={0}
                            aria-label="price changed within days"
                            placeholder="days"
                            value={priceChangedWithinDays || undefined}
                            style={{ width: 80 }}
                            onChange={value => this.load({ page: 1, changes: { priceChangedWithinDays: typeof value === "number" && value >= 1 ? value : null } })}
                        />{" "}
                        {QUICK_DAYS.map(days => (
                            <Button key={days} size="small" type={priceChangedWithinDays === days ? "primary" : "default"} style={{ marginRight: 4 }}
                                onClick={() => this.load({ page: 1, changes: { priceChangedWithinDays: priceChangedWithinDays === days ? null : days } })}>
                                {days} days
                            </Button>
                        ))}
                    </span>
                    <Checkbox checked={expired} onChange={e => this.load({ page: 1, changes: { expired: e.target.checked } })}>Expired only</Checkbox>
                    <Checkbox checked={dropped} onChange={e => this.load({ page: 1, changes: { dropped: e.target.checked } })}>Dropped only</Checkbox>
                </div>
                {!readOnly && (selectedIds.length > 0 || allMatching) && this.renderBulkBar()}
                <Table
                    rowKey="_id"
                    size="small"
                    loading={loading}
                    dataSource={lines}
                    columns={this.columns()}
                    scroll={{ x: 1800 }}
                    expandedRowRender={line => <PriceHistory line={line} uploadLabel={uploadLabel} />}
                    rowSelection={readOnly ? undefined : {
                        selectedRowKeys: allMatching ? lines.map(l => l._id) : selectedIds,
                        onChange: keys => this.setState({ selectedIds: keys, allMatching: false })
                    }}
                    pagination={{ current: page, pageSize, total }}
                    onChange={(pagination, filters, sorter) => {
                        const sort = sorter && sorter.order ? "lastPriceChangeAt" : "";
                        this.load(sort !== this.state.sort ? { page: 1, changes: { sort } } : { page: pagination.current });
                    }}
                />
                {!readOnly && (
                    <div style={{ color: MUTED, fontSize: 12 }}>Click a price or MOQ to edit it in place · Enter or clicking away saves · Esc cancels · expand a row for its price history</div>
                )}
                {bulkAction && (
                    <BulkModal
                        action={bulkAction}
                        count={allMatching ? total : selectedIds.length}
                        onRun={this.runBulk}
                        onCancel={() => this.setState({ bulkAction: null })}
                    />
                )}
                {editing && (
                    <LineDrawer
                        priceList={priceList}
                        line={editing === "new" ? null : editing}
                        onClose={() => this.setState({ editing: null })}
                        onSaved={() => {
                            this.setState({ editing: null });
                            this.load();
                        }}
                    />
                )}
            </div>
        );
    }
}

export default PriceListLines;



// WEBPACK FOOTER //
// ./src/components/supplier-price-list/lines.js